import React, { useState, useEffect } from 'react';
import { 
  Users, 
  User,
  Heart,
  Brain,
  Target,
  MapPin,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Eye,
  MessageSquare,
  ShoppingCart,
  Smartphone,
  Mail,
  Calendar,
  Star,
  Zap,
  Clock,
  RefreshCw,
  Send,
  X,
  Info,
  ChevronRight,
  ChevronDown,
  Lightbulb,
  BarChart3,
  Activity,
  Briefcase,
  DollarSign,
  Coffee,
  Gamepad2,
  Stethoscope,
  FileText,
  Phone,
  Search
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Color palette aligned with main site
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// Journey stage colors and emotions
const JOURNEY_STAGES = {
  awareness: { label: 'Awareness', color: 'blue', icon: Eye, bgClass: 'bg-blue-50', borderClass: 'border-blue-200', textClass: 'text-blue-700', iconBg: 'bg-blue-100' },
  consideration: { label: 'Consideration', color: 'amber', icon: Brain, bgClass: 'bg-amber-50', borderClass: 'border-amber-200', textClass: 'text-amber-700', iconBg: 'bg-amber-100' },
  purchase: { label: 'Purchase', color: 'green', icon: ShoppingCart, bgClass: 'bg-green-50', borderClass: 'border-green-200', textClass: 'text-green-700', iconBg: 'bg-green-100' },
  experience: { label: 'Experience', color: 'purple', icon: Heart, bgClass: 'bg-purple-50', borderClass: 'border-purple-200', textClass: 'text-purple-700', iconBg: 'bg-purple-100' },
  loyalty: { label: 'Loyalty', color: 'pink', icon: Star, bgClass: 'bg-pink-50', borderClass: 'border-pink-200', textClass: 'text-pink-700', iconBg: 'bg-pink-100' }
};

// Business scenario examples
const BUSINESS_SCENARIOS = [
  {
    title: "Project Management Software",
    description: "SaaS project management tool for remote teams and freelancers",
    icon: <Zap className="h-4 w-4" />,
    color: "bg-blue-50 text-blue-700 border-blue-200",
    category: "Technology"
  },
  {
    title: "Sustainable Fashion Brand",
    description: "Direct-to-consumer eco-friendly clothing for conscious consumers",
    icon: <ShoppingCart className="h-4 w-4" />,
    color: "bg-green-50 text-green-700 border-green-200",
    category: "Retail"
  },
  {
    title: "Digital Banking Service",
    description: "Mobile banking app designed for young professionals and entrepreneurs",
    icon: <DollarSign className="h-4 w-4" />,
    color: "bg-amber-50 text-amber-700 border-amber-200",
    category: "Fintech"
  },
  {
    title: "Fitness & Wellness Platform",
    description: "Personal training and nutrition coaching through mobile app",
    icon: <Activity className="h-4 w-4" />,
    color: "bg-red-50 text-red-700 border-red-200",
    category: "Health & Fitness"
  },
  {
    title: "Online Learning Platform",
    description: "Professional skill development courses for career advancement",
    icon: <Brain className="h-4 w-4" />,
    color: "bg-purple-50 text-purple-700 border-purple-200",
    category: "Education"
  },
  {
    title: "Artisan Coffee Subscription",
    description: "Monthly delivery of specialty coffee beans from local roasters",
    icon: <Coffee className="h-4 w-4" />,
    color: "bg-orange-50 text-orange-700 border-orange-200",
    category: "Food & Beverage"
  }
];

// Enhanced Button component
const Button = ({ 
  children, 
  onClick, 
  isLoading = false, 
  fullWidth = false, 
  variant = 'primary', 
  icon, 
  className = '', 
  disabled = false,
  size = 'md'
}) => {
  const baseStyle = "font-medium transition-all duration-300 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500";
  
  const sizes = {
    sm: "py-2 px-4 text-sm rounded-md",
    md: "py-3 px-6 text-base rounded-lg",
    lg: "py-4 px-8 text-lg rounded-xl"
  };
  
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    outline: `bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
      ) : (
        icon && <span className="mr-2">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification with better mobile support
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div className={`fixed top-20 left-4 right-4 sm:right-4 sm:left-auto sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${styles[type]}`}>
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
      <p className="font-medium flex-1">{message}</p>
      <button 
        onClick={onClose} 
        className="flex-shrink-0 p-1 hover:bg-black/10 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Business Scenario Card with mobile improvements
const ScenarioCard = ({ scenario, onClick }) => {
  return (
    <button
      onClick={() => onClick(scenario)}
      className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-red-300 hover:shadow-sm transition-all w-full group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg border ${scenario.color} flex items-center justify-center`}>
          {scenario.icon}
        </div>
        <div className="text-xs text-gray-500 group-hover:text-red-600 transition-colors font-medium">
          Try this example →
        </div>
      </div>
      <div className="mb-2">
        <p className="text-sm font-medium text-gray-900 mb-1">{scenario.title}</p>
        <span className="text-xs text-gray-500 font-medium">{scenario.category}</span>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed">
        {scenario.description}
      </p>
    </button>
  );
};

// Persona Display Component with mobile improvements
const PersonaDisplay = ({ persona }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
            {persona.name?.charAt(0) || 'P'}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{persona.name}</h3>
            <p className="text-gray-600 text-sm sm:text-base">{persona.title}</p>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0"
            aria-label={expanded ? "Collapse persona details" : "Expand persona details"}
          >
            {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <p className="text-gray-700 mb-4 leading-relaxed">{persona.description}</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-600 mb-1">Age & Location</div>
            <div className="font-semibold text-gray-900">{persona.age}, {persona.location}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-600 mb-1">Income Level</div>
            <div className="font-semibold text-gray-900">{persona.income}</div>
          </div>
        </div>

        {expanded && (
          <div className="space-y-6 pt-4 border-t border-gray-200">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-green-600" />
                Key Goals & Motivations
              </h4>
              <ul className="space-y-2">
                {persona.goals?.map((goal, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                    <span className="leading-relaxed">{goal}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Main Frustrations & Pain Points
              </h4>
              <ul className="space-y-2">
                {persona.frustrations?.map((frustration, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0"></div>
                    <span className="leading-relaxed">{frustration}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Journey Stage Display with mobile improvements
const JourneyStageDisplay = ({ stage, stageKey }) => {
  const stageInfo = JOURNEY_STAGES[stageKey] || JOURNEY_STAGES.awareness;
  const StageIcon = stageInfo.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <article className={`bg-white rounded-xl border ${stageInfo.borderClass} shadow-sm hover:shadow-md transition-all duration-300`}>
      <div className={`${stageInfo.bgClass} px-4 sm:px-6 py-4 border-b ${stageInfo.borderClass}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={`w-10 h-10 rounded-lg ${stageInfo.iconBg} flex items-center justify-center flex-shrink-0`}>
              <StageIcon className={`h-5 w-5 ${stageInfo.textClass}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-900 text-base sm:text-lg">{stageInfo.label}</h3>
              <p className="text-sm text-gray-600 truncate">{stage.emotion}</p>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0"
            aria-label={expanded ? "Collapse stage details" : "Expand stage details"}
          >
            {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        <p className="text-gray-700 mb-4 leading-relaxed">{stage.description}</p>
        
        <div className="mb-4">
          <h4 className="font-semibold text-gray-900 mb-3">What customers do:</h4>
          <ul className="space-y-2">
            {stage.actions?.slice(0, 3).map((action, idx) => (
              <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                <ArrowRight className="h-3 w-3 mt-1 text-gray-400 flex-shrink-0" />
                <span className="leading-relaxed">{action}</span>
              </li>
            ))}
          </ul>
        </div>

        {expanded && (
          <div className="space-y-6 pt-4 border-t border-gray-200">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Pain Points & Challenges
              </h4>
              <ul className="space-y-2">
                {stage.painPoints?.map((pain, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0"></div>
                    <span className="leading-relaxed">{pain}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-600" />
                Improvement Opportunities
              </h4>
              <ul className="space-y-2">
                {stage.opportunities?.map((opp, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0"></div>
                    <span className="leading-relaxed">{opp}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Loading Animation with better mobile support
const LoadingAnimation = () => {
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(1);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 - prev) / 10;
        return Math.min(newProgress, 99);
      });
      
      setStep(prev => {
        if (prev === 1 && progress > 30) return 2;
        if (prev === 2 && progress > 65) return 3;
        return prev;
      });
    }, 600);
    
    return () => clearInterval(interval);
  }, [progress]);
  
  const steps = [
    { message: "Analyzing business characteristics...", icon: <Search className="h-5 w-5 text-red-600" /> },
    { message: "Creating customer personas...", icon: <Users className="h-5 w-5 text-red-600" /> },
    { message: "Mapping customer journey stages...", icon: <MapPin className="h-5 w-5 text-red-600" /> }
  ];
  
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sm:p-8 max-w-md mx-auto text-center">
      <div className="mb-8">
        <div className="relative mx-auto w-20 h-20 sm:w-24 sm:h-24">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              className="text-gray-200"
              strokeWidth="8"
              stroke="currentColor"
              fill="transparent"
              r="42"
              cx="50"
              cy="50"
            />
            <circle
              className="text-red-600 transition-all duration-500 ease-out"
              strokeWidth="8"
              strokeDasharray={264}
              strokeDashoffset={264 - (progress / 100) * 264}
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r="42"
              cx="50"
              cy="50"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-lg sm:text-xl font-bold text-gray-900">{Math.round(progress)}%</div>
              <div className="text-xs text-gray-500">Complete</div>
            </div>
          </div>
        </div>
      </div>
      
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
        Creating Customer Intelligence
      </h3>
      
      <div className="inline-flex items-center gap-3 bg-red-50 text-red-700 px-4 py-2 sm:px-5 sm:py-3 rounded-full mb-6 border border-red-200">
        {steps[step - 1].icon}
        <span className="font-medium text-sm sm:text-base">{steps[step - 1].message}</span>
      </div>
      
      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
        This takes about 30 seconds while we analyze customer behavior patterns and create detailed personas with journey mapping.
      </p>
    </div>
  );
};

// Email Capture with mobile improvements
const EmailCapture = ({ onSubmit, isSubmitting }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  const handleSubmit = () => {
    if (!email || !email.includes('@')) return;
    onSubmit({ email, name });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <FileText className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Get Complete Customer Intelligence Report</h3>
        <p className="text-gray-600 text-sm">Download detailed personas, journey maps, and optimization recommendations with implementation guide</p>
      </div>
      
      <div className="mb-6 space-y-3">
        <h4 className="font-medium text-gray-900 text-sm">Your report includes:</h4>
        {[
          "Detailed customer persona profiles with demographics and psychographics",
          "Complete journey maps with pain points and opportunities at each stage", 
          "Specific optimization recommendations for improving customer experience",
          "Implementation checklist with priority actions and success metrics"
        ].map((item, idx) => (
          <div key={idx} className="flex items-start gap-3 text-sm text-gray-600">
            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
            <span className="leading-relaxed">{item}</span>
          </div>
        ))}
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={!email || !email.includes('@')}
          isLoading={isSubmitting}
          icon={<Send className="h-4 w-4" />}
          fullWidth
        >
          Send Customer Intelligence Report
        </Button>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        We respect your privacy and never share your information
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Customer Intelligence Report Sent!</h3>
      <p className="text-gray-600 mb-4">
        Check your email at <span className="font-semibold text-gray-900">{email}</span> for your complete customer intelligence report with detailed personas and journey optimization recommendations.
      </p>
      <p className="text-gray-600 text-sm">
        Our customer experience experts will follow up to help you implement these insights and improve your customer journey.
      </p>
    </div>
  );
};

// Main Component
const CustomerJourneyMapper = () => {
  const [customBusiness, setCustomBusiness] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [journeyMap, setJourneyMap] = useState(null);
  const [toast, setToast] = useState(null);
  const [analysisTitle, setAnalysisTitle] = useState('');
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const analyzeCustomerJourney = async (businessDescription) => {
    setIsLoading(true);
    setAnalysisTitle(businessDescription);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 3500,
          temperature: 0.7,
          messages: [
            {
              role: 'user',
              content: `Create a customer intelligence analysis for this business: "${businessDescription}"

Generate:
1. **Primary Customer Persona** - the main target customer
2. **Customer Journey Map** - how they move from awareness to loyalty

For the PERSONA, include:
- Name and professional title
- Age, location, and income level
- 2-sentence description of who they are
- 4-5 key goals/motivations  
- 4-5 main frustrations/pain points

For the JOURNEY MAP across 5 stages (awareness, consideration, purchase, experience, loyalty), include for each stage:
- Brief description of what happens in this stage
- Customer emotion (e.g., "Curious but overwhelmed", "Excited but cautious")
- 4-5 key actions they take during this stage
- 3-4 pain points they experience
- 3-4 optimization opportunities for improvement

Make this realistic and specific. Keep language simple and business-focused.

Respond in this exact JSON format:
{
  "persona": {
    "name": "Sarah Johnson",
    "title": "Marketing Manager",
    "age": 32,
    "location": "Austin, TX",
    "income": "$65,000-$80,000",
    "description": "A tech-savvy marketing professional who manages a team of 5 at a growing SaaS company. She's always looking for tools that can help her team work more efficiently and deliver better results.",
    "goals": [
      "Increase team productivity and collaboration",
      "Deliver better marketing results with less effort", 
      "Find tools that integrate well with existing workflow",
      "Prove ROI of marketing investments to leadership"
    ],
    "frustrations": [
      "Too many disconnected tools that don't talk to each other",
      "Spending too much time on manual tasks instead of strategy",
      "Difficulty tracking and measuring team performance",
      "Budget constraints and need to justify every purchase"
    ]
  },
  "journey": {
    "awareness": {
      "description": "First realizes they have a problem that needs solving",
      "emotion": "Curious but overwhelmed",
      "actions": [
        "Searches Google for solutions to their problem",
        "Asks colleagues for recommendations",
        "Reads industry blogs and articles",
        "Joins relevant online communities"
      ],
      "painPoints": [
        "Too many options to choose from",
        "Unclear which solutions actually work",
        "Information overload from multiple sources"
      ],
      "opportunities": [
        "Create helpful educational content",
        "Optimize for relevant search terms",
        "Build thought leadership presence"
      ]
    },
    "consideration": {
      "description": "Evaluates different solutions and compares options",
      "emotion": "Motivated but cautious",
      "actions": [
        "Compares features and pricing of different solutions",
        "Reads reviews and case studies",
        "Requests demos or free trials",
        "Discusses options with team members"
      ],
      "painPoints": [
        "Difficulty comparing solutions objectively",
        "Sales pressure from multiple vendors",
        "Fear of making the wrong choice"
      ],
      "opportunities": [
        "Provide clear comparison guides",
        "Offer transparent pricing",
        "Share authentic customer testimonials"
      ]
    },
    "purchase": {
      "description": "Makes the decision to buy and completes the transaction",
      "emotion": "Excited but anxious",
      "actions": [
        "Negotiates terms and pricing",
        "Gets approval from stakeholders",
        "Completes purchase process",
        "Sets up initial account"
      ],
      "painPoints": [
        "Complex procurement processes",
        "Unclear contract terms",
        "Long setup procedures"
      ],
      "opportunities": [
        "Streamline onboarding process",
        "Provide clear documentation",
        "Offer dedicated support during setup"
      ]
    },
    "experience": {
      "description": "Uses the product and integrates it into their workflow",
      "emotion": "Hopeful but learning",
      "actions": [
        "Learns how to use the product effectively",
        "Trains team members on new tool",
        "Integrates with existing systems",
        "Measures initial results"
      ],
      "painPoints": [
        "Steep learning curve",
        "Integration challenges",
        "Lack of immediate results"
      ],
      "opportunities": [
        "Improve user onboarding",
        "Provide comprehensive training",
        "Offer ongoing support and guidance"
      ]
    },
    "loyalty": {
      "description": "Becomes a satisfied customer and potential advocate",
      "emotion": "Confident and satisfied",
      "actions": [
        "Recommends product to peers",
        "Expands usage within organization",
        "Provides feedback for improvements",
        "Renews subscription or contract"
      ],
      "painPoints": [
        "Product limitations in advanced use cases",
        "Pricing concerns for expanded usage",
        "Desire for additional features"
      ],
      "opportunities": [
        "Build customer advocacy programs",
        "Develop advanced features",
        "Create referral incentives"
      ]
    }
  }
}`
            }
          ]
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze customer journey');
      }

      const data = await response.json();
      const resultContent = data.content[0].text;
      
      const jsonMatch = resultContent.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const analysisData = JSON.parse(jsonMatch[0]);
        
        setTimeout(() => {
          setPersonas([analysisData.persona]);
          setJourneyMap(analysisData.journey || {});
          setIsLoading(false);
        }, 2000);
      } else {
        throw new Error('Failed to parse analysis data');
      }
    } catch (error) {
      console.error('Error analyzing customer journey:', error);
      setIsLoading(false);
      setToast({
        message: 'Unable to analyze customer journey. Please try again.',
        type: 'error'
      });
    }
  };

  const handleCustomAnalysis = () => {
    if (!customBusiness.trim()) {
      setToast({
        message: 'Please describe your business first.',
        type: 'error'
      });
      return;
    }
    analyzeCustomerJourney(customBusiness);
  };

  const handleEmailSubmit = (data) => {
    setIsEmailSubmitting(true);
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(data.email);
      setToast({
        message: 'Customer intelligence report sent to your email successfully!',
        type: 'success'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };

  const handleReset = () => {
    setCustomBusiness('');
    setPersonas([]);
    setJourneyMap(null);
    setEmailSubmitted(false);
    setSubmittedEmail('');
    setAnalysisTitle('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectScenario = (scenario) => {
    setCustomBusiness(scenario.description);
    const textarea = document.getElementById('business-input');
    if (textarea) {
      setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }, 100);
    }
  };

  // SEO metadata (would be handled by helmet in real implementation)
  useEffect(() => {
    document.title = "Customer Journey Mapper - Persona & Journey Analysis Tool | Lusidea";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Create detailed customer personas and journey maps. Understand your customers pain points, motivations, and optimize their experience across all touchpoints.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header Section */}
      <header className="bg-white pt-24 sm:pt-28 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <Users className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Customer Journey Mapper
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Understand your customers with detailed personas and journey maps. Identify pain points, opportunities, and optimize their experience at every touchpoint
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Input Section */}
        {!isLoading && personas.length === 0 && !emailSubmitted && (
          <div className="space-y-8">
            {/* Primary Input */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Describe Your Business or Service
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Tell us what you do and who your customers are. We'll create detailed personas and map their complete journey.
              </p>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="business-input" className="block text-sm font-medium text-gray-700 mb-3">
                    Business description
                  </label>
                  <textarea
                    id="business-input"
                    value={customBusiness}
                    onChange={(e) => setCustomBusiness(e.target.value)}
                    placeholder="Describe your business, product, or service. For example: 'We're a SaaS project management tool for remote marketing teams' or 'We sell sustainable fashion online to eco-conscious millennials' or 'We provide accounting software for small restaurant owners'..."
                    className="w-full p-4 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none min-h-32 text-gray-800 resize-y"
                    rows={6}
                  />
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 gap-2">
                    <span className="text-sm text-gray-500">Be specific about your business and target customers for better insights</span>
                    <span className="text-sm text-gray-400">{customBusiness.length} characters</span>
                  </div>
                </div>
                
                <Button
                  onClick={handleCustomAnalysis}
                  disabled={!customBusiness.trim()}
                  icon={<Search className="h-5 w-5" />}
                  fullWidth
                  size="lg"
                >
                  Analyze Customer Journey
                </Button>
              </div>
            </section>

            {/* Example Suggestions */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                Need inspiration? Try these business examples:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {BUSINESS_SCENARIOS.map((scenario, idx) => (
                  <ScenarioCard
                    key={idx}
                    scenario={scenario}
                    onClick={handleSelectScenario}
                  />
                ))}
              </div>
            </section>
            
            {/* How it works */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-3">How this tool works</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Our analysis system examines your business description and creates detailed customer personas with complete journey mapping. 
                    You'll get insights into customer motivations, pain points, and specific opportunities to improve their experience at each stage 
                    from awareness through loyalty.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Loading State */}
        {isLoading && <LoadingAnimation />}

        {/* Results */}
        {!isLoading && personas.length > 0 && !emailSubmitted && (
          <div className="space-y-8">
            {/* Results Header */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Customer Analysis Complete</h2>
                  <p className="text-gray-600 text-sm">
                    Customer insights for: <span className="font-medium">"{analysisTitle.slice(0, 100)}..."</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Journey Stages Overview */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-center">Customer Journey Stages</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {Object.entries(JOURNEY_STAGES).map(([key, stage]) => {
                  const StageIcon = stage.icon;
                  return (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50">
                      <div className={`w-10 h-10 rounded-lg ${stage.iconBg} flex items-center justify-center mb-2`}>
                        <StageIcon className={`h-5 w-5 ${stage.textClass}`} />
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{stage.label}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Customer Persona */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <User className="h-6 w-6 text-blue-600" />
                Your Primary Customer
              </h2>
              {personas.map((persona, idx) => (
                <PersonaDisplay key={idx} persona={persona} />
              ))}
            </section>

            {/* Customer Journey */}
            {journeyMap && (
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <MapPin className="h-6 w-6 text-purple-600" />
                  Customer Journey Map
                </h2>
                <div className="space-y-6">
                  {Object.entries(journeyMap).map(([stageKey, stage]) => (
                    <JourneyStageDisplay
                      key={stageKey}
                      stage={stage}
                      stageKey={stageKey}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Email Capture */}
            <section className="max-w-2xl mx-auto">
              <EmailCapture 
                onSubmit={handleEmailSubmit}
                isSubmitting={isEmailSubmitting}
              />
            </section>

            {/* Quick Wins Section */}
            <section className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Implementation Tips</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
                  <div className="bg-white rounded-lg p-4">
                    <CheckCircle className="h-5 w-5 text-green-600 mb-2" />
                    <h4 className="font-medium text-gray-900 mb-2">Reduce Purchase Friction</h4>
                    <p className="text-sm text-gray-600">Focus on simplifying your checkout and onboarding process to reduce drop-offs</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <CheckCircle className="h-5 w-5 text-green-600 mb-2" />
                    <h4 className="font-medium text-gray-900 mb-2">Improve Early Experience</h4>
                    <p className="text-sm text-gray-600">Enhance onboarding and initial product experience to boost satisfaction</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <CheckCircle className="h-5 w-5 text-green-600 mb-2" />
                    <h4 className="font-medium text-gray-900 mb-2">Address Awareness Pain Points</h4>
                    <p className="text-sm text-gray-600">Create better educational content to help customers discover your solution</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Expert Consultation CTA */}
            <section className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Need Expert Guidance?</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Get personalized advice from our customer experience experts on implementing these insights. 
                  We'll help you prioritize improvements and create an action plan.
                </p>
                <a
                  href="https://calendly.com/lusidea/customer-experience-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm"
                >
                  <Calendar className="h-5 w-5" />
                  Schedule Expert Consultation
                </a>
              </div>
            </section>

            {/* Reset Action */}
            <div className="text-center pt-4">
              <Button
                onClick={handleReset}
                variant="outline"
                icon={<RefreshCw className="h-4 w-4" />}
              >
                Analyze Different Business
              </Button>
            </div>
          </div>
        )}

        {/* Success State */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      <style jsx global>{`
        /* Enhanced focus states for accessibility */
        input:focus, textarea:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </div>
  );
};

export default CustomerJourneyMapper;